import { X } from 'lucide-react';
import React from 'react';

/**
 * Badge Component
 *
 * @param {string} [props.colour='primary'] - The colour of the badge ('brand', 'primary', 'info', 'success', 'warning', 'danger', or a custom Tailwind color)
 * @param {string} [props.size='md'] - The size of the badge ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {React.ReactNode} props.children - The content to be rendered inside the badge
 * @param {React.ReactElement} [props.leftIcon] - The icon to be rendered on the left side of the badge
 * @param {React.ReactElement} [props.rightIcon] - The icon to be rendered on the right side of the badge
 * @param {function} [props.onRemove] - Callback function when remove button is clicked
 * @param {string} [props.shape='badge'] - The shape of the component ('badge', 'pill')
 * @param {Object} props - The properties passed to the component
 * @returns {React.ReactElement} A badge element with the specified properties
 */
const Badge = ({
  shape = 'badge',
  colour = 'primary',
  size = 'md',
  leftIcon: LeftIcon,
  children,
  rightIcon: RightIcon,
  onRemove,
  className = '',
  ...props
}) => {
  const sizeClasses = {
    xs: 'px-1 py-0.5 text-xs',
    sm: 'px-2 py-1 text-xs',
    md: 'px-2 py-1 text-xs',
    lg: 'px-2 py-1.5 text-sm',
    xl: 'px-3 py-2 text-sm'
  };

  const iconClasses = {
    xs: 'h-3 w-3',
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
    xl: 'h-5 w-5'
  };

  const colourClasses = {
    brand: 'bg-brand-50 text-brand-700 ring-brand-600/20',
    primary: 'bg-primary-50 text-primary-700 ring-primary-600/20',
    info: 'bg-info-50 text-info-700 ring-info-600/20',
    success: 'bg-success-50 text-success-700 ring-success-600/20',
    warning: 'bg-warning-50 text-warning-700 ring-warning-600/20',
    danger: 'bg-danger-50 text-danger-700 ring-danger-600/20',
    slate: 'bg-slate-50 text-slate-700 ring-slate-600/20',
    gray: 'bg-gray-50 text-gray-700 ring-gray-600/20',
    zinc: 'bg-zinc-50 text-zinc-700 ring-zinc-600/20',
    neutral: 'bg-neutral-50 text-neutral-700 ring-neutral-600/20',
    stone: 'bg-stone-50 text-stone-700 ring-stone-600/20',
    red: 'bg-red-50 text-red-700 ring-red-600/20',
    orange: 'bg-orange-50 text-orange-700 ring-orange-600/20',
    amber: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    yellow: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
    lime: 'bg-lime-50 text-lime-700 ring-lime-600/20',
    green: 'bg-green-50 text-green-700 ring-green-600/20',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    teal: 'bg-teal-50 text-teal-700 ring-teal-600/20',
    cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
    sky: 'bg-sky-50 text-sky-700 ring-sky-600/20',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
    violet: 'bg-violet-50 text-violet-700 ring-violet-600/20',
    purple: 'bg-purple-50 text-purple-700 ring-purple-600/20',
    fuchsia: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-600/20',
    pink: 'bg-pink-50 text-pink-700 ring-pink-600/20',
    rose: 'bg-rose-50 text-rose-700 ring-rose-600/20'
  };

  const shapeClasses = {
    badge: 'rounded-md',
    pill: 'rounded-full'
  };

  const removeButtonClasses = {
    brand: {
      badge:
        'hover:bg-brand-600/20 stroke-brand-600/50 group-hover:stroke-brand-600/75 rounded-sm',
      pill: 'hover:bg-brand-600/20 stroke-brand-600/50 group-hover:stroke-brand-600/75 rounded-full'
    },
    primary: {
      badge:
        'hover:bg-primary-600/20 stroke-primary-600/50 group-hover:stroke-primary-600/75 rounded-sm',
      pill: 'hover:bg-primary-600/20 stroke-primary-600/50 group-hover:stroke-primary-600/75 rounded-full'
    },
    info: {
      badge:
        'hover:bg-info-600/20 stroke-info-600/50 group-hover:stroke-info-600/75 rounded-sm',
      pill: 'hover:bg-info-600/20 stroke-info-600/50 group-hover:stroke-info-600/75 rounded-full'
    },
    success: {
      badge:
        'hover:bg-success-600/20 stroke-success-600/50 group-hover:stroke-success-600/75 rounded-sm',
      pill: 'hover:bg-success-600/20 stroke-success-600/50 group-hover:stroke-success-600/75 rounded-full'
    },
    warning: {
      badge:
        'hover:bg-warning-600/20 stroke-warning-600/50 group-hover:stroke-warning-600/75 rounded-sm',
      pill: 'hover:bg-warning-600/20 stroke-warning-600/50 group-hover:stroke-warning-600/75 rounded-full'
    },
    danger: {
      badge:
        'hover:bg-danger-600/20 stroke-danger-600/50 group-hover:stroke-danger-600/75 rounded-sm',
      pill: 'hover:bg-danger-600/20 stroke-danger-600/50 group-hover:stroke-danger-600/75 rounded-full'
    }
  };

  const getColourClasses = colour => {
    return colourClasses[colour] || colourClasses['primary'];
  };

  const getRemoveButtonClasses = (colour, shape) => {
    if (removeButtonClasses[colour]) {
      return removeButtonClasses[colour][shape];
    }
    return removeButtonClasses['primary'][shape];
  };

  return (
    <span
      className={`inline-flex items-center gap-x-1 ${
        sizeClasses[size]
      } ${getColourClasses(colour)} ${
        shapeClasses[shape]
      } ring-1 ring-inset whitespace-nowrap font-medium ${className}`}
      {...props}
    >
      {LeftIcon && <LeftIcon className={`${iconClasses[size]} mr-1`} />}
      {children}
      {RightIcon && <RightIcon className={`${iconClasses[size]} ml-1`} />}
      {onRemove && (
        <button
          type='button'
          className={`${getRemoveButtonClasses(colour, shape)}`}
          onClick={onRemove}
        >
          <span className='sr-only'>Remove</span>
          <X size={14} />
        </button>
      )}
    </span>
  );
};

export { Badge };
